/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useContext, useState } from 'react';
import {
  Typography,
  Descriptions,
  Button,
  Modal,
  Tabs,
  Dropdown,
  Menu,
  Alert,
} from 'antd';
import {
  InfoCircleOutlined,
  BarsOutlined,
  CodeOutlined,
  DownOutlined,
} from '@ant-design/icons';
import styles from './styles.module.css';
import JSONPretty from 'react-json-pretty';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import WeakSignalByProvider from '../../../../../../../../../public/images/WeakSignalByProvider.png';
import HistoricallyWeakSignalByProvider from '../../../../../../../../../public/images/HistoricallyWeakSignalByProvider.png';
import MobileConnectivityDetails from '../../../../../../../../../public/images/Mobile_connectivity_details.png';
import WifiConnectivityDetails from '../../../../../../../../../public/images/Wifi_connectivity_details.png';
import NoAccessDescriptionModal from '../../../../../../components/NoAccessDescriptionModal';
import ConfigContext from '../../../../../../../../components/ConfigContext';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const jsonTheme = {
  main: 'line-height:1.3;color:#d8d8d8;overflow:auto;',
  error: 'line-height:1.3;color:#d8d8d8;overflow:auto;',
  key: 'color:#007b98;',
  string: 'color:#656565;',
  value: 'color:#7cb304;',
  boolean: 'color:#ffa718;',
};

const bytesToSize = bytes => {
  let sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) {
    return '0 Byte';
  }
  // eslint-disable-next-line radix
  let i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
};

const msToTime = millieseconds => {
  let days = 24 * 60 * 60 * 1000;
  let hours = 60 * 60 * 1000;
  let minutes = 60 * 1000;
  let seconds = 1000;

  let time = '';
  let timeParser = millieseconds;

  if (timeParser > days) {
    time = Math.trunc(timeParser / days) + 'd ';
    timeParser = timeParser % days;
  }
  if (timeParser > hours) {
    time += Math.trunc(timeParser / hours) + 'h ';
    timeParser = timeParser % hours;
  }
  if (timeParser > minutes) {
    time += Math.trunc(timeParser / minutes) + 'm ';
    timeParser = timeParser % minutes;
  }
  if (timeParser > seconds) {
    time += Math.trunc(timeParser / seconds) + 's ';
    timeParser = timeParser % seconds;
  }
  return time;
};

const getItem = (label, key, icon, children, type) => {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
};

export const DeviceDetails = props => {
  const { deviceInfo } = props;
  const { config } = useContext(ConfigContext);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [advancedItem, setAdvancedItem] = useState({
    isViewNoAccessModal: false,
  });
  const { t } = useTranslation();
  const items = [
    getItem(t('label_weakSignalByOperator'), 'signal'),
    getItem(t('label_weakSignalHistoryByOperator'), 'signal_history'),
    getItem(t('label_mobileConnectivityDetails'), 'mobile'),
    getItem(t('label_networkProviderVariation'), 'network_provider'),
  ];
  const menu = <Menu onClick={e => handleAdvancedMenuClick(e)} items={items} />;

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleAdvancedMenuClick = event => {
    console.log('click', event);
    const menuKey = event.key;
    let title;
    let content;
    let imgSrc;
    let redirectPage;

    switch (menuKey) {
      case 'no_sim':
        redirectPage =
          window.location.origin +
          `/${config.appName}/analytics/device-connectivity/wifi-connectivity?operator=Sim%20Card%20not%20detected`;
        title = t('label_noSim');
        imgSrc = WifiConnectivityDetails;
        content = (
          <div>
            <Alert message={t('analytic_noSim_warningTxt')} type="warning" />
          </div>
        );
        break;
      case 'signal':
        redirectPage =
          window.location.origin +
          `/${config.appName}/analytics/device-connectivity/mobile-connectivity?` +
          `operator=${deviceInfo.operator}&panelId=5`;
        title = t('label_weakSignalByOperator');
        imgSrc = WeakSignalByProvider;
        content = (
          <div>
            <Alert
              message={t('analytic_warningTxt1', {
                label: t('label_weakSignalByOperator'),
              })}
              type="warning"
            />
          </div>
        );
        break;
      case 'signal_history':
        redirectPage =
          window.location.origin +
          `/${config.appName}/analytics/device-connectivity/mobile-connectivity?` +
          `operator=${deviceInfo.operator}&panelId=6`;
        title = t('label_weakSignalHistoryByOperator');
        imgSrc = HistoricallyWeakSignalByProvider;
        content = (
          <div>
            <Alert
              message={t('analytic_warningTxt1', {
                label: t('label_weakSignalHistoryByOperator'),
              })}
              type="warning"
            />
          </div>
        );
        break;
      case 'mobile':
        redirectPage =
          window.location.origin +
          `/${config.appName}/analytics/device-connectivity/mobile-connectivity?operator=All`;
        title = t('label_mobileConnectivityDetails');
        imgSrc = MobileConnectivityDetails;
        content = (
          <div>
            <Alert
              message={t('analytic_warningTxt1', {
                label: t('label_mobileConnectivityDetails'),
              })}
              type="warning"
            />
          </div>
        );
        break;
      case 'network_provider':
        redirectPage =
          window.location.origin +
          `/${config.appName}/analytics/device-connectivity/mobile-connectivity?operator=${deviceInfo.operator}`;
        title = t('label_networkProviderVariation');
        imgSrc = MobileConnectivityDetails;
        content = (
          <div>
            <Alert
              message={t('analytic_warningTxt1', {
                label: t('label_networkProviderVariation'),
              })}
              type="warning"
            />
          </div>
        );
        break;
    }

    if (config.analyticEnabledDomains.includes(config.userDomain)) {
      window.open(redirectPage, '_blank');
    } else {
      setAdvancedItem({
        title: title,
        imgSrc: imgSrc,
        content: content,
        isViewNoAccessModal: true,
      });
    }
  };

  const onCloseAdvancedNoAccessModal = () => {
    setAdvancedItem({
      isViewNoAccessModal: false,
    });
  };

  return (
    <div className={styles.content}>
      <Title level={4} className={styles.title}>
        <InfoCircleOutlined />
        {t('label_deviceDetails')}
      </Title>
      <Descriptions column={{ sm: 2, md: 4 }} layout="vertical" size="small">
        {deviceInfo.hasOwnProperty('AGENT_VERSION') && (
          <Descriptions.Item
            label={<Text strong>{t('label_agentVersion')}</Text>}
          >
            {deviceInfo.AGENT_VERSION}
          </Descriptions.Item>
        )}
        {deviceInfo.hasOwnProperty('vendor') && (
          <Descriptions.Item label={<Text strong>{t('label_vendor')}</Text>}>
            {deviceInfo.vendor}
          </Descriptions.Item>
        )}
        {deviceInfo.hasOwnProperty('deviceModel') && (
          <Descriptions.Item
            label={<Text strong>{t('label_deviceModel')}</Text>}
          >
            {deviceInfo.deviceModel}
          </Descriptions.Item>
        )}
        {deviceInfo.hasOwnProperty('encryptionEnabled') && (
          <Descriptions.Item
            label={<Text strong>{t('label_encryptionStatus')}</Text>}
          >
            {deviceInfo.encryptionEnabled ? 'enabled' : 'disabled'}
          </Descriptions.Item>
        )}
        {deviceInfo.hasOwnProperty('IS_ROOTED') && (
          <Descriptions.Item
            label={<Text strong>{t('label_rootStatus')}</Text>}
          >
            {deviceInfo.IS_ROOTED}
          </Descriptions.Item>
        )}
        {deviceInfo.hasOwnProperty('PhoneNumber') && (
          <Descriptions.Item
            label={<Text strong>{t('label_phoneNumber')}</Text>}
          >
            {deviceInfo.PhoneNumber}
          </Descriptions.Item>
        )}
        {deviceInfo.hasOwnProperty('operator') && (
          <Descriptions.Item label={<Text strong>{t('label_operator')}</Text>}>
            {deviceInfo.operator === 'Sim Card not detected' && (
              <Dropdown
                overlay={
                  <Menu onClick={e => handleAdvancedMenuClick(e)}>
                    <Menu.Item key="no_sim">{t('label_noSim')}</Menu.Item>
                  </Menu>
                }
              >
                <Button
                  type="primary"
                  size={'small'}
                  style={{
                    borderRadius: '20px',
                  }}
                >
                  {deviceInfo.operator} <DownOutlined />
                </Button>
              </Dropdown>
            )}
            {deviceInfo.operator !== 'Sim Card not detected' && (
              <Dropdown overlay={menu}>
                <Button
                  type="primary"
                  size={'small'}
                  style={{
                    borderRadius: '20px',
                  }}
                >
                  {deviceInfo.operator} <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </Descriptions.Item>
        )}
        {deviceInfo.hasOwnProperty('serial') && (
          <Descriptions.Item label={<Text strong>{t('label_serial')}</Text>}>
            {deviceInfo.serial}
          </Descriptions.Item>
        )}
      </Descriptions>
      <div className={styles.viewAllButton}>
        <Button type="dashed" size="small" onClick={showModal}>
          {t('label_viewAll')}
        </Button>
      </div>
      <Modal
        title={t('label_deviceDetails')}
        className={styles.deviceDetailsModal}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        maskClosable={false}
      >
        <Tabs defaultActiveKey="formatted">
          <TabPane
            tab={
              <span>
                <BarsOutlined />
                {t('label_formatted')}
              </span>
            }
            key="formatted"
          >
            <Descriptions
              column={1}
              size="small"
              bordered={true}
              className={styles.formattedItems}
            >
              {Object.keys(deviceInfo).map(key => {
                let data = deviceInfo[key];

                switch (key) {
                  case 'totalRAMMemory':
                  case 'availableRAMMemory':
                  case 'WIFI_DATA_USAGE':
                  case 'ramThreshold':
                    // eslint-disable-next-line radix
                    data = bytesToSize(parseInt(data));
                    break;
                  case 'batteryLevel':
                    data = data + ' %';
                    break;
                  case 'mobileSignalStrength':
                    data = data + 'ASU';
                    break;
                  case 'osBuildDate':
                  case 'TIME_LAST_REBOOT':
                  case 'OS_UPDATE_FIRST_AVAILABILITY':
                    // eslint-disable-next-line radix
                    data = moment(parseInt(data)).format('LLLL');
                    break;
                  case 'DISTANCE':
                    data = data + ' m';
                    break;
                  case 'batteryVoltage':
                    data = data + ' V';
                    break;
                  case 'wifiSignalStrength':
                    data = data + ' RSSI';
                    break;
                  case 'externalTotalMemory':
                  case 'internalTotalMemory':
                  case 'externalAvailableMemory':
                  case 'internalAvailableMemory':
                    data = data.toString() + ' GB';
                    break;
                  case 'DEVICE_TEMPERATURE_GPU':
                  case 'DEVICE_TEMPERATURE_CPU':
                  case 'DEVICE_TEMPERATURE_SKIN':
                    data = data + ' °C';
                    break;
                  case 'DEVICE_AVERAGE_CPU_USAGE':
                  case 'TIME_SINCE_LAST_REBOOT':
                    // eslint-disable-next-line radix
                    data = msToTime(parseInt(data));
                    break;
                }
                return (
                  <Descriptions.Item label={key} key={key}>
                    <p>
                      {typeof deviceInfo[key] === 'object'
                        ? JSON.stringify(deviceInfo[key])
                        : data}
                    </p>
                  </Descriptions.Item>
                );
              })}
            </Descriptions>
          </TabPane>
          <TabPane
            tab={
              <span>
                <CodeOutlined />
                {t('label_raw')}
              </span>
            }
            key="raw"
          >
            <JSONPretty id="json-pretty" theme={jsonTheme} data={deviceInfo} />
          </TabPane>
        </Tabs>
      </Modal>
      {!config.analyticEnabledDomains.includes(config.userDomain) &&
        advancedItem.isViewNoAccessModal && (
          <div>
            <NoAccessDescriptionModal
              title={advancedItem.title}
              content={advancedItem.content}
              imgSrc={advancedItem.imgSrc}
              isModalView={advancedItem.isViewNoAccessModal}
              onCloseAdvancedNoAccessModal={onCloseAdvancedNoAccessModal}
            />
          </div>
        )}
    </div>
  );
};

export default DeviceDetails;
